/**
 * Conversation state layer for the ChatBox surface (ADR 0007): OUR side owns the active thread id, the
 * controlled `messages` array, and the thread list — ChatBox just renders. Reuses the ai-teammate
 * React-Query hooks/keys so both surfaces share one cache; history seeds the controlled array by
 * DERIVATION (no state-sync effects). The array splits as seeded (derived) + live (state); turn ids
 * arrive via the adapter's `onFinishMetadata` tap. Managed-agent-only: every turn sends agentType `managed`.
 */

import { useQueryClient } from "@tanstack/react-query"
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react"
import { useDispatch } from "react-redux"
import { v4 } from "uuid"
import { log } from "helpers/logger"
import { Creators } from "model/apiWrapper"
import { useAccountAlias } from "resources/account"
import { useKellyAnalytics } from "./use-kelly-analytics"
import type { CreateAiSdkAdapterRequest } from "@mui/x-chat"
import type { ChatAdapter, ChatMessage } from "@mui/x-chat-headless"
import { StreamResumeError } from "connected-components/ai-teammate/utils/chat-transport"
import { AI_TEAMMATE_EVENTS } from "connected-components/ai-teammate/analytics/events"
import { SHARED_EVENTS } from "connected-components/analytics/shared/events"
import { MANAGED_AGENT_TYPE, NUDGE_PROMPTS } from "connected-components/ai-teammate/helpers/constants"
import type { Thread } from "connected-components/ai-teammate/types/thread"
import { buildMessageList } from "connected-components/ai-teammate/utils/message-list"
import { getMessagesQueryKey } from "connected-components/ai-teammate/utils/message-cache"
import { addThreadToCache } from "connected-components/ai-teammate/utils/threads-cache"
import { useInfiniteGetQuery } from "hooks/queries"
import { SUGGESTED_PROMPT_TEXTS } from "../constants"
import {
  forgetComposerDraft,
  markDraftThreadAdopted,
  pruneComposerDrafts,
  readComposerDraft,
  seedNewChatDraft,
  writeComposerDraft,
} from "./composer-drafts"
import { assembleAssistantMessage, createChatboxAdapter } from "./adapter"
import { createBufferedChatStream } from "./buffered-transport"
import {
  PENDING_NEW_THREAD_KEY,
  adoptPendingUserTurn,
  forgetPendingUserTurn,
  getPendingUserTurn,
  pendingUserChatMessage,
  rememberPendingUserTurn,
} from "./pending-user-turn"
import { createStreamingChatStream, reattachActiveStream, withActiveTurn } from "./streaming-transport"
import type { TurnOutcome } from "./streaming-transport"
import { useChatTransport } from "./use-chat-transport"
import { LOCAL_TURN_IN_PROGRESS_CODE, describeChatApiError, isExpectedStreamGoneError, safeErrorType } from "./chat-api-errors"
import { findLastAssistantMessageId, historyMessagesToChatMessages } from "./history-to-messages"
import { type ChatFinishMetadata, messageText } from "./normalize-chunks"
import { useActiveTurn } from "./use-active-turn"
import { useLatest, useSyncRef } from "./use-latest"
import { useLiveMessages } from "./use-live-messages"
import { useThreadList } from "./use-thread-list"

// Matches the drawer's page size for the shared messages query (ai-teammate context/messages.jsx).
const MESSAGES_PER_PAGE = 50
const EMPTY_MESSAGES: ChatMessage[] = []
const EMPTY_SEEDED_IDS: ReadonlySet<string> = new Set()

// Every suggested-prompt text (starter list + "Browse topics" categories), so a clicked topic prompt classifies
// as suggested, not typed (APP-3404). Derived from the same drawer constants the empty state renders.
const SUGGESTED_PROMPT_SET: ReadonlySet<string> = new Set(SUGGESTED_PROMPT_TEXTS)

// Verbatim nudge prompts keep their "nudge" analytics attribution.
const NUDGE_PROMPT_SET: ReadonlySet<string> = new Set(NUDGE_PROMPTS.map((prompt) => prompt.text))

/**
 * The legacy drawer's typed-vs-suggested distinction on the CHAT_MESSAGE_SENT payload: a verbatim suggested
 * prompt is `"suggested_prompt"`, anything else is `"typed"`. The drawer reset the source on every keystroke,
 * so an edited prompt counted as typed — a text-identity check reproduces that (an edit no longer matches).
 */
function classifyMessageSource(text: string): "typed" | "suggested_prompt" | "nudge" {
  if (NUDGE_PROMPT_SET.has(text.trim())) return "nudge"
  return SUGGESTED_PROMPT_SET.has(text.trim()) ? "suggested_prompt" : "typed"
}

/** A raw history page as pi-server serializes it; consumed by the shared infinite query. */
interface RawHistoryPage {
  messages?: unknown[]
  nextMessageId?: unknown
  hasMoreBefore?: unknown
}

/** The backward cursor of a page, narrowed (`null`/absent -> no older page). */
function nextCursorOf(page: RawHistoryPage): string | undefined {
  return typeof page.nextMessageId === "string" ? page.nextMessageId : undefined
}

/** The fixed send payload — a typed shape so a mistyped key can't compile silently. modelId is optional:
 * op-summarize treats it as optional for the managed agent (#4125), so an empty one is OMITTED, never sent null. */
interface ChatSendBody {
  message: string
  agentArguments: { modelId?: string, agentType: string }
  lastAssistantMsgId?: string | null
}

export interface ChatConversationsController {
  threads: Thread[]
  /** True once the thread list reached a terminal state — success OR error (never hang a gate). */
  isThreadsLoaded: boolean
  isThreadsErrored: boolean
  activeThreadId: string | null
  /** Read the saved composer draft for a draft key (empty string when none) — the draft bridge restores it on remount. */
  readDraft: (key: string) => string
  /** Mirror the composer's unsent text under a draft key; an empty value drops the entry (a sent/cleared composer keeps no draft). */
  writeDraft: (key: string, value: string) => void
  /** Remount key for ChatBox — changes only on explicit navigation, never mid-turn. */
  navKey: string
  messages: ChatMessage[]
  onMessagesChange: (messages: ChatMessage[]) => void
  adapter: ChatAdapter
  selectThread: (threadId: string) => void
  startNewThread: () => void
  /**
   * Programmatic send — drives the SAME adapter/transport path the composer uses; rejects with the calm
   * classified error on failure.
   */
  sendMessage: (text: string) => Promise<void>
  /** A turn (composer OR programmatic) is streaming — agent-native parity for the programmatic path. */
  isTurnActive: boolean
  /** Abort the active turn (composer or programmatic); the composer's own Stop button uses the runtime path for a clean cancelled render. */
  stopTurn: () => void
  /** Server-side stop: DELETE the thread's active stream so op-summarize halts (APP-3394). The composer's Stop fires this alongside its runtime abort; no-op until #4141 lands cancelStream on the shared transport. */
  stopServerStream: () => void
  /**
   * The transcript surface mounted. Re-probes the active thread for a live stream — the resume latch is
   * per-thread, so without this a return to a thread we never left would never reattach (ADR 0018).
   */
  attachSurface: () => void
  /**
   * The transcript surface unmounted. A turn's lifetime is the surface's visit: abort it, drop its
   * uncommitted output, and run the same detach `navigateToThread` runs for a thread it leaves — otherwise
   * the partial the MUI runtime froze on unmount renders as a settled answer next visit (ADR 0018).
   */
  detachSurface: () => void
  /** Reattaching to a thread's in-flight stream on select — the composer send path is blocked while true. */
  isReattaching: boolean
  /** Reattach handshake in progress — true only until the first reattached content arrives; drives the "Reconnecting" indicator. */
  isReconnecting: boolean
  hasOlder: boolean
  isLoadingHistory: boolean
  isLoadingOlder: boolean
  historyError: string | null
  loadOlder: () => void
  renameThread: (threadId: string, title: string) => Promise<void>
  /**
   * Pre-fill the New-chat composer with a prompt raised elsewhere in the app (the nav nudge). Loses to text
   * already in that bucket, and navigates to the New chat so the draft bridge actually restores it (ADR 0018).
   */
  seedComposerDraft: (text: string) => void
  /** Toggle a thread's pin: optimistic flip, rollback + notify on failure, persisted via PATCH (APP-3367). */
  pinThread: (threadId: string) => boolean
  /** True once the thread data carries the `pinned` field — the gate that reveals the pin UI. */
  canPinThreads: boolean
  /** Delete a thread: optimistic removal, rollback + notify on failure, never awaited (PROD-452). */
  deleteThread: (threadId: string) => void
  /** Id of the thread with a rename/pin in flight, or null — lets each row scope its own busy state. */
  mutatingThreadId: string | null
}

export interface UseChatConversationsOptions {
  /** The account's selected/default risk model, or null. Sent in agentArguments ONLY when present — op-summarize treats it as optional and asks the user conversationally when absent (#4125). */
  modelId: string | null
  /**
   * Transport selection. Streaming is unconditional in production; `"buffered"` is a test/degradation
   * seam the stories set (a real runtime kill-switch would need pi-server negative-flag support — see ADR 0007).
   */
  transportMode?: "streaming" | "buffered"
  /**
   * False keeps the controller mounted but idle — the thread-list query does not run. The provider is
   * app-level now, so an account that has never opened Kelly must not pay for it (ADR 0018).
   */
  isActive?: boolean
}

export function useChatConversations({ modelId, transportMode = "streaming", isActive = true }: UseChatConversationsOptions): ChatConversationsController {
  const accountAlias = useAccountAlias()
  const dispatch = useDispatch()
  const queryClient = useQueryClient()

  // The shared SSE transport (same class the ai-teammate drawer streams through).
  const streamingTransport = useChatTransport()

  const [activeThreadId, setActiveThreadId] = useState<string | null>(null)
  const [navSeq, setNavSeq] = useState(0)
  // Turns produced in this view (composer or programmatic); seeded history is derived, not state.
  // The ref is declared up here because useLiveMessages consumes it, but the seeded ids derive further down.
  const seededIdsRef = useRef<ReadonlySet<string>>(EMPTY_SEEDED_IDS)
  const {
    liveMessages,
    liveMessagesRef,
    onMessagesChange,
    appendLiveMessage,
    appendReplyTo,
    upsertLiveMessage,
    discardLiveMessage,
    markLiveMessageErrored,
    discardAllLiveMessages,
    clearLiveMessages,
    resetLiveMessagesForNavigation,
    clearDiscardedLiveIds,
    parkFinalAnswer,
    discardParkedFinalAnswer,
    resetFinalAnswers,
  } = useLiveMessages(seededIdsRef)
  // The last SERVER assistant-message id (the continue concurrency guard); the runtime assembles live turns under synthetic ids.
  const [serverAssistantId, setServerAssistantId] = useState<string | null>(null)
  // Threads ADOPTED (created new) in this session: their content lives entirely in the controlled
  // array with no server history to fetch, and the server list may lag them. Not continues on
  // existing threads — those keep their fetched history and must stay subject to delete reconciliation.
  const [adoptedThreadIds, setAdoptedThreadIds] = useState<ReadonlySet<string>>(() => new Set())

  // Bumped when the transcript surface mounts. The resume probe latches per thread, and nothing else in its
  // deps changes across a surface remount onto the SAME thread — this is the re-probe trigger (ADR 0018).
  const [surfaceSeq, setSurfaceSeq] = useState(0)

  const [isReattaching, setIsReattaching] = useState(false)
  // Handshake-only: true while opening the reattach stream, cleared the instant the first content arrives
  // (onProgress). Drives the "Reconnecting" indicator, which must hide once the stream is actually flowing —
  // unlike isReattaching, which stays true for the whole reattached turn (it blocks the composer send).
  const [isReconnecting, setIsReconnecting] = useState(false)

  const adoptedThreadIdsRef = useLatest(adoptedThreadIds)
  // The current view's thread id, for callbacks that must guard against the SEND-TIME closure (which lags navigation).
  const activeThreadIdRef = useLatest(activeThreadId)
  // How many transcript surfaces are mounted (see "Surface presence" below). Declared up here because the turn
  // settle has to ask whether anything is on screen before it moves the view.
  const surfaceCountRef = useRef(0)

  // ---- Per-conversation composer drafts -------------------------------------------------------
  // The map lives in composer-drafts.ts because it must survive this controller unmounting (ADR 0015), and it is
  // not state because the bridge writes through on every keystroke. The prune exemption is deliberately not
  // `adoptedThreadIds` — that doubles as the history-fetch gate, so nav-away clears it.
  const readDraft = useCallback((key: string) => readComposerDraft(accountAlias, key), [accountAlias])
  const writeDraft = useCallback((key: string, value: string) => writeComposerDraft(accountAlias, key, value), [accountAlias])

  // ---- Active turn (unified across composer + programmatic sends) ------------------------------

  const { isTurnActive, beginTurn, endTurn, stopTurn, stopServerStream, getTurnSignal, getActiveTurn } =
    useActiveTurn(streamingTransport, activeThreadIdRef)
  // Every navigation aborts the in-flight client read (navSeq bumps only on nav — early adoption / finish leave it
  // untouched). navigateToThread aborts synchronously; this backstops the render-phase deleted-thread reset, which
  // can't call stopTurn in render. Aborting also suppresses that orphaned turn's finish tap (getTurnSignal), so it
  // can't re-poison the fresh view's continue guard. The server stream survives for attach-on-select resume.
  // stopTurn is a stable empty-dep callback, so navSeq is the only real trigger — keep it stable or this fires per render.
  useEffect(() => {
    stopTurn()
    resetFinalAnswers()
  }, [navSeq, stopTurn, resetFinalAnswers])

  // ---- Thread list (shared cache with the ai-teammate drawer) ---------------------------------

  const {
    threads,
    threadsKey,
    isThreadsLoaded,
    isThreadsErrored,
    threadsError,
    canPinThreads,
    mutatingThreadId,
    renameThread,
    pinThread,
    deleteThreadMutation,
  } = useThreadList(accountAlias, isActive)

  useEffect(() => {
    if (!isThreadsLoaded || isThreadsErrored) return
    pruneComposerDrafts(accountAlias, new Set(threads.map((thread) => thread.threadId)))
  }, [accountAlias, threads, isThreadsLoaded, isThreadsErrored])

  // ---- History (the drawer's paginated messages query, shared key) ----------------------------

  const messagesQueryKey = useMemo(() => getMessagesQueryKey(accountAlias, activeThreadId), [accountAlias, activeThreadId])
  // Cache presence gates fetching (mirrors the drawer): an existing entry is the source of truth and must
  // not be refetched over. Read it through useSyncExternalStore — with the cache shared across two surfaces
  // (ADR 0007), a direct getQueryData() in render tears in concurrent mode (AGENTS.md).
  const subscribeToQueryCache = useCallback((onStoreChange: () => void) => queryClient.getQueryCache().subscribe(onStoreChange), [queryClient])
  const hasCachedData = useSyncExternalStore(subscribeToQueryCache, () => queryClient.getQueryData(messagesQueryKey) !== undefined)
  // A thread we created live this session already holds its turns in the controlled array; skip its
  // history fetch on the OWN flag rather than seeding an empty shared-cache entry (that would blank the drawer).
  const isLiveOnlyActiveThread = activeThreadId != null && adoptedThreadIds.has(activeThreadId)

  const {
    data: historyData,
    isFetching: isFetchingHistory,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error: historyFetchError,
  } = useInfiniteGetQuery<RawHistoryPage, string | undefined>({
    url: `/api/${accountAlias}/ai/chat/threads/${activeThreadId}?limit=${MESSAGES_PER_PAGE}`,
    key: messagesQueryKey,
    enabled: !!activeThreadId && !hasCachedData && !isLiveOnlyActiveThread,
    staleTime: Infinity,
    getNextPageParam: nextCursorOf,
    // Forward pagination (newer-than-first-page) is not a thing on this endpoint.
    getPreviousPageParam: () => undefined,
    initialPageParam: undefined,
  })

  // Derived seeding (no sync effect, no race): pages are keyed by thread id, so a late response can never seed the wrong thread.
  // A live-only thread keeps its turns in liveMessages; ignore any shared-cache history for it (React Query still returns
  // cached data with the fetch disabled), or another surface filling that cache would concatenate a duplicate of the same turns.
  const derivedSeed = useMemo(
    () => (isLiveOnlyActiveThread ? EMPTY_MESSAGES : historyMessagesToChatMessages(buildMessageList(historyData))),
    [historyData, isLiveOnlyActiveThread],
  )

  // While live turns are on screen, cap the seed at the newest history message observed BEFORE the first of
  // them (APP-3386). A cross-surface refetch — the drawer viewing this thread through the shared key — re-derives
  // the just-sent turn under its REAL server id while liveMessages still holds it under a synthetic one, and the
  // id dedupe below can't match the two, so the turn renders twice. Capping rather than freezing keeps "Load
  // older" working: older pages land before the ceiling and still flow in. The cost is that a message another
  // surface adds mid-visit stays hidden until navigation — the same self-healing boundary as the live turns.
  const [seedCeilingId, setSeedCeilingId] = useState<string | null>(null)
  // Render-phase guarded state (converges: each branch's predicate is false once applied). Only a non-empty seed
  // sets a ceiling — an empty one has nothing to duplicate, and a continue can't outrun history anyway (its guard
  // comes from the seed, so buildTurnBody throws 409 until history has loaded).
  if (liveMessages.length > 0 && seedCeilingId == null && derivedSeed.length > 0) {
    setSeedCeilingId(derivedSeed[derivedSeed.length - 1].id)
  } else if (liveMessages.length === 0 && seedCeilingId != null) {
    setSeedCeilingId(null)
  }
  const seededMessages = useMemo(() => {
    if (seedCeilingId == null) return derivedSeed
    const ceilingIndex = derivedSeed.findIndex((message) => message.id === seedCeilingId)
    // Ceiling absent from a fresh page set (deleted elsewhere, or a page boundary moved): take the full seed
    // rather than truncate to nothing — a duplicate turn beats a conversation that renders empty.
    return ceilingIndex < 0 ? derivedSeed : derivedSeed.slice(0, ceilingIndex + 1)
  }, [derivedSeed, seedCeilingId])

  // A resumed turn's optimistic prompt has no server id to dedupe on, so it rides its reply's committed id: once
  // history seeds that reply, the prompt beside it is a duplicate too. Reachable only when a commit races this
  // visit's history fetch — a turn that ended BEFORE the probe has no active stream, so its prompt is dropped.
  const [resumedPromptPair, setResumedPromptPair] = useState<{ promptId: string, assistantId: string } | null>(null)

  const seededIds = useMemo(() => new Set(seededMessages.map((message) => message.id)), [seededMessages])

  // Drop any live message whose id is now seeded: resume can upsert a reattached turn into liveMessages
  // before history loads, and once that same server id arrives in seededMessages the raw concat would
  // show it twice (upsertLiveMessage only dedupes against what was seeded AT write time).
  const messages = useMemo(() => {
    const supersededPromptId = resumedPromptPair != null && seededIds.has(resumedPromptPair.assistantId)
      ? resumedPromptPair.promptId
      : null
    return [
      ...seededMessages,
      ...liveMessages.filter((message) => !seededIds.has(message.id) && message.id !== supersededPromptId),
    ]
  }, [seededMessages, seededIds, liveMessages, resumedPromptPair])
  // The continue guard when no live turn has finished yet: the last assistant turn in history.
  const lastSeededAssistantId = useMemo(() => findLastAssistantMessageId(seededMessages), [seededMessages])

  useSyncRef<ReadonlySet<string>>(seededIdsRef, seededIds)
  const messagesRef = useLatest(messages)

  // One descriptor, so the reader's copy and the event's class cannot drift apart.
  const historyErrorDescriptor = useMemo(
    () => (historyFetchError != null ? describeChatApiError(historyFetchError) : null),
    [historyFetchError],
  )
  const historyError = historyErrorDescriptor?.message ?? null

  // ---- Sends: buffered transport behind the adapter seam --------------------------------------

  const postJson = useCallback(
    (url: string, body: unknown, signal: AbortSignal) =>
      new Promise<unknown>((resolve, reject) => dispatch(Creators.post(url, body, resolve, reject, signal))),
    [dispatch],
  )

  // Send-time analytics (APP-3383). The dropped route-mount provider is replaced by the same helper the
  // provider used, with the drawer's context properties (threadId, modelId), so CHAT_MESSAGE_SENT stays
  // schema-identical to what the drawer still fires, plus the "Kelly" prefix (APP-3438).
  // threadId is omitted (undefined, not null) on a first send, matching the drawer.
  const analyticsProperties = useMemo(
    () => ({ threadId: activeThreadId ?? undefined, modelId: modelId ?? undefined }),
    [activeThreadId, modelId],
  )
  const analytics = useKellyAnalytics(analyticsProperties)
  // Send-time CHAT_MESSAGE_SENT — invoked by each transport opener right before the dispatch, once the body
  // built and the gates passed. Kept OUT of buildTurnBody so that stays a pure builder (a schema-critical
  // event as a side effect of a body build would double-fire the first time the builder is reused).
  const trackSend = useCallback(
    (userText: string) => analytics.track(AI_TEAMMATE_EVENTS.CHAT_MESSAGE_SENT, { source: classifyMessageSource(userText) }),
    [analytics],
  )

  /**
   * Turn outcomes (APP-3572). Reported from the transport wrapper rather than here, because that wrapper is the
   * only thing every turn passes through; this just maps an outcome onto an event. A failure becomes the shared
   * `Error` with an `operation`, so a fifth failing operation later adds a value rather than a fifth event name.
   */
  const trackTurnOutcome = useCallback((outcome: TurnOutcome) => {
    if (outcome.status === "failed") {
      const serverErrorType = safeErrorType(outcome.errorType)
      analytics.track(SHARED_EVENTS.ERROR, {
        operation: "send",
        // Always this app's taxonomy — the same vocabulary the history and thread_list failures below report,
        // and the same one whichever transport carried the turn. `describeChatApiError` is the fallback only:
        // for a server-side failure it sees rendered copy, which buckets every one of them as "unknown".
        kind: outcome.kind ?? describeChatApiError(outcome.error).kind,
        // The server's own class, where it named one. Six of them collapse into `unavailable`, so this is what
        // keeps a tool timeout distinguishable from a model-API failure once the taxonomy has grouped them.
        ...(serverErrorType != null && { errorType: serverErrorType }),
        durationMs: outcome.durationMs,
      })
      return
    }
    analytics.track(AI_TEAMMATE_EVENTS.TURN_COMPLETED, {
      durationMs: outcome.durationMs,
      transport: transportMode === "buffered" ? "buffered" : "streaming",
    })
  }, [analytics, transportMode])
  /**
   * The two load failures. Keyed on the failure's identity rather than fired from the render that derives it,
   * so a re-render with the same error standing does not re-report it. `resume` is deliberately absent: those
   * are dominated by expected stream-gone cases (isExpectedStreamGoneError) that the code already treats as
   * non-events, and reporting them would bury the real failures.
   */
  const historyErrorKind = historyErrorDescriptor?.kind ?? null
  const analyticsRef = useLatest(analytics)
  useEffect(() => {
    if (historyErrorKind == null) return
    analyticsRef.current.track(SHARED_EVENTS.ERROR, { operation: "history", kind: historyErrorKind })
  }, [historyErrorKind, analyticsRef])
  // Same taxonomy bucket as `send` and `history`, so all three operations slice one vocabulary.
  const threadsErrorKind = isThreadsErrored ? describeChatApiError(threadsError).kind : null
  useEffect(() => {
    if (threadsErrorKind == null) return
    analyticsRef.current.track(SHARED_EVENTS.ERROR, { operation: "thread_list", kind: threadsErrorKind })
  }, [threadsErrorKind, analyticsRef])

  // The adapter's callbacks are deliberately REF-FREE (react-hooks/refs): send context comes from state/props, so the adapter is recreated when it changes.
  const continueGuardId = serverAssistantId ?? lastSeededAssistantId

  // One body for both transports (start vs continue): agentType rides start AND continue so a thread
  // continues on the agent it was created with; lastAssistantMsgId is the continue concurrency guard.
  // modelId is included ONLY when present — op-summarize treats it as optional (#4125) and asks the user
  // conversationally when absent, so an empty one is omitted from the wire, never posted as null.
  // Pure builder — it validates (the continue guard) and throws, but has no side effects; the
  // CHAT_MESSAGE_SENT emit lives in the openers after this returns.
  const buildTurnBody = useCallback(
    (userText: string): ChatSendBody => {
      const body: ChatSendBody = {
        message: userText,
        agentArguments: modelId ? { modelId, agentType: MANAGED_AGENT_TYPE } : { agentType: MANAGED_AGENT_TYPE },
      }
      if (activeThreadId) {
        // A continue MUST carry the guard (pi-server requires lastAssistantMsgId); a missing one — e.g. a
        // stopped first turn adopted with no messageId — would post an invalid body and 400.
        // No seeded history means this thread was adopted mid-turn and that turn never settled, which is
        // still-stopping; a seeded thread that simply has no assistant row is a genuine out-of-sync.
        if (continueGuardId == null) {
          const stillStopping = seededMessages.length === 0
          throw Object.assign(
            new Error("continue is missing the last-assistant id"),
            stillStopping ? { code: 409, error: { code: LOCAL_TURN_IN_PROGRESS_CODE } } : { code: 409 },
          )
        }
        body.lastAssistantMsgId = continueGuardId
      }
      return body
    },
    [activeThreadId, modelId, continueGuardId, seededMessages],
  )

  const postChatMessage = useCallback(
    (userText: string, signal: AbortSignal) => {
      const url = activeThreadId
        ? `/api/${accountAlias}/ai/chat/threads/${activeThreadId}`
        : `/api/${accountAlias}/ai/chat/threads`
      const body = buildTurnBody(userText)
      trackSend(userText)
      return postJson(url, body, signal)
    },
    [accountAlias, postJson, activeThreadId, buildTurnBody, trackSend],
  )

  // Streaming SSE turn: the shared transport chooses the start vs continue stream route from chatId.
  const openStreamingTurn = useCallback(
    (userText: string, signal: AbortSignal) => {
      const body = buildTurnBody(userText)
      trackSend(userText)
      // Park the prompt: op-summarize persists it only when the turn commits and the replay carries no user event,
      // so this is the ONLY copy that can survive nav-away — without it resume renders an answer with no question.
      rememberPendingUserTurn(activeThreadId ?? PENDING_NEW_THREAD_KEY, userText)
      return streamingTransport.sendMessages({ chatId: activeThreadId, body, abortSignal: signal })
    },
    [streamingTransport, activeThreadId, buildTurnBody, trackSend],
  )

  // The one metadata path for BOTH transports: capture the continue guard and adopt a new thread's server id.
  // One owner for the guard-write rule, called by both the finish and the failed-turn taps: it encodes a
  // race carve-out, so two copies would let a correction land on one and miss the other. A turn belongs to
  // this view when it names no thread, names the live one, or is a genuine new-chat first turn — the ref is
  // mirrored by useSyncRef in an effect, so it still reads null while that turn is adopting.
  const turnBelongsToCurrentView = useCallback(
    (turnThreadId: string | null | undefined): boolean => {
      const isNewChatFirstTurn = activeThreadId == null && activeThreadIdRef.current == null
      return turnThreadId == null || turnThreadId === activeThreadIdRef.current || isNewChatFirstTurn
    },
    [activeThreadId, activeThreadIdRef],
  )

  const handleFinishMetadata = useCallback(
    (metadata: ChatFinishMetadata, request: CreateAiSdkAdapterRequest) => {
      const finishedThreadId = metadata.threadId
      // The guard write feeds the CURRENT view's next continue, so gate it on the live thread id (ref), not the
      // send-time closure — a turn finishing after navigation must not poison the new view's guard. The null-ref
      // allowance is scoped to a genuine new-chat send (closure id also null): a render-phase deleted-thread reset
      // nulls the live ref too, and its abort lands a tick later (post-commit effect), so an orphaned finish could
      // otherwise slip through that window and re-poison the fresh empty view.
      const belongsToCurrentView = turnBelongsToCurrentView(finishedThreadId)
      if (metadata.messageId != null && belongsToCurrentView) {
        // A reply without an id keeps the PRIOR guard rather than nulling it.
        setServerAssistantId(metadata.messageId)
      }
      if (metadata.text != null && metadata.text.trim() !== "" && belongsToCurrentView) {
        parkFinalAnswer(metadata.text)
      }
      // Committed (a messageId): op-summarize wrote the user row too, so history owns this prompt from now on.
      // Ungated by the view — the turn committed wherever the user went. A first send that never reached
      // thread_created still has its prompt under the New-chat key, so clear that too.
      if (metadata.messageId != null) {
        forgetPendingUserTurn(finishedThreadId ?? activeThreadId ?? PENDING_NEW_THREAD_KEY)
        if (activeThreadId == null) forgetPendingUserTurn(PENDING_NEW_THREAD_KEY)
      }
      if (finishedThreadId == null) return
      // Only a NEW thread is adopted (send-time activeThreadId null) — a continue on an existing thread keeps
      // its fetched history and stays subject to delete reconciliation.
      if (activeThreadId != null) return

      // DATA (correct wherever the view moved): the thread exists server-side, so optimistically add it to the
      // shared list — it belongs in the sidebar even if the sender navigated away before this finished. Must
      // land BEFORE any active-id flip so the deleted-thread reconciliation never sees an unknown active thread.
      addThreadToCache(queryClient, threadsKey, {
        threadId: finishedThreadId,
        title: messageText(request.message).trim() || "New chat",
        updatedAt: new Date().toISOString(),
        agentType: MANAGED_AGENT_TYPE,
        // `canPinThreads` reads the row's shape, so omitting this hides pinning until a refetch (APP-3598).
        pinned: false,
      })

      // VIEW HIJACK — idempotency + defence-in-depth (APP-3404). The adapter already suppresses this tap for an
      // aborted turn (getTurnSignal), so this guards the live-view case: adopt into the view ONLY if it is still a
      // New chat (ref). Once early adoption has flipped the active id, this same guard makes the finish idempotent
      // — it skips the re-adoption while its unguarded addThreadToCache above still lands the title.
      if (activeThreadIdRef.current != null) return
      // The addThreadToCache above lands it in the list, but an already-in-flight refetch can still resolve
      // over that optimistic write — keep the draft exempt until a list we observe actually carries it.
      markDraftThreadAdopted(accountAlias, finishedThreadId)
      setAdoptedThreadIds((current) => (current.has(finishedThreadId) ? current : new Set(current).add(finishedThreadId)))
      setActiveThreadId(finishedThreadId)
    },
    [queryClient, threadsKey, activeThreadId, accountAlias, activeThreadIdRef, parkFinalAnswer, turnBelongsToCurrentView],
  )

  // Early adoption (APP-3404): the `thread_created` event carries the new thread's id BEFORE final_message, so
  // flip the view onto it mid-stream — WITHOUT a ChatBox remount (navSeq untouched, like handleFinishMetadata),
  // so the streaming answer keeps rendering. Mirrors the view writes below the guard in handleFinishMetadata;
  // that later finish is idempotent — its own `activeThreadIdRef.current != null` guard now sees the adopted id and
  // skips the re-adoption (its unguarded addThreadToCache still runs, landing the thread + title in the sidebar).
  const handleEarlyThreadId = useCallback(
    (earlyThreadId: string, request: CreateAiSdkAdapterRequest): string | null => {
      // Only a NEW thread emits thread_created (send-time activeThreadId null); a continue never does, and its
      // thread is already in the sidebar. The adapter suppresses this tap for an aborted turn.
      if (activeThreadId != null) return null

      // DATA (correct wherever the view moves): thread_created means pi-server persisted the thread (aiChatStream
      // observe->persistThread), so add the Recents row IMMEDIATELY — the sidebar row must appear when the turn
      // STARTS, not at first response, and it belongs in the list even if the user navigates away before finish
      // (APP-3376). Forced managed agentType so it survives the sidebar's managed-only filter; upsert-by-id, so the
      // later finish write can't duplicate it. Lands above the view guard, mirroring handleFinishMetadata.
      addThreadToCache(queryClient, threadsKey, {
        threadId: earlyThreadId,
        title: messageText(request.message).trim() || "New chat",
        updatedAt: new Date().toISOString(),
        agentType: MANAGED_AGENT_TYPE,
        pinned: false,
      })
      // Re-key the parked prompt onto the real id now the thread exists, so a resume probe on it can find it.
      adoptPendingUserTurn(earlyThreadId)

      // VIEW: adopt into the view ONLY if it's still a New chat (ref). If the user already navigated, the row
      // above still landed; skip the view hijack (return null so this turn's settle isn't scoped to an adoption).
      if (activeThreadIdRef.current != null) return null
      // The row above is an optimistic write an in-flight refetch can still resolve over — keep the draft written
      // under the freshly adopted id exempt until a list we observe actually carries it.
      markDraftThreadAdopted(accountAlias, earlyThreadId)
      setAdoptedThreadIds((current) => (current.has(earlyThreadId) ? current : new Set(current).add(earlyThreadId)))
      setActiveThreadId(earlyThreadId)
      // Return the adopted id so the adapter scopes it to THIS turn's settle (turn-scoped, not a shared ref).
      return earlyThreadId
    },
    [activeThreadId, queryClient, threadsKey, accountAlias, activeThreadIdRef],
  )

  // Turn settle (APP-3404 R1): stream drained. If this turn early-adopted a thread but never committed, reset the
  // VIEW so the aborted turn can't glue onto the next send. The sidebar row survives — the thread persists in
  // pi-server (thread_created ran persistThread, APP-3376), so the draft exemption stays too. Both halves —
  // the adoption marker and the active-id + transcript reset — are surface-gated.
  const handleTurnSettled = useCallback((committed: boolean, earlyThreadId: string | null) => {
    // earlyThreadId is scoped to THIS turn by the adapter (closure, not a shared ref), so a later turn's early
    // adoption can't be rolled back here even though endTurn frees the active-turn ref before flush reaches settle.
    if (earlyThreadId == null || committed) return
    // A detach aborts the turn, so the settle it triggers is an uncommitted one landing INSIDE the detached
    // window — where the marker is the thread's only defence (it gates the history fetch off mid-generation and
    // exempts the thread from deleted-thread reconciliation). attachSurface clears it on the way back in.
    if (surfaceCountRef.current === 0) return
    setAdoptedThreadIds((current) => {
      if (!current.has(earlyThreadId)) return current
      const next = new Set(current)
      next.delete(earlyThreadId)
      return next
    })
    // Still on the now-idle thread: reset the view AND the controlled transcript. Leaving the aborted turn in
    // liveMessages would glue it onto the NEXT send's fresh START. If the user already navigated away, nav wiped
    // both — so gate on the live id (ref) and no-op otherwise. (Detach is covered by the surface guard above:
    // it preserves the active id, so this comparison is true there too, and the reset would drop the user onto
    // New chat while the thread it left carries on committing in the sidebar.)
    if (activeThreadIdRef.current === earlyThreadId) {
      setActiveThreadId(null)
      // Clearing our copy isn't enough: ChatBox still holds the aborted turn, and its next onMessagesChange is an
      // unconditional replace that would put it straight back. Suppress those ids until a nav remounts ChatBox.
      discardAllLiveMessages()
    }
  }, [activeThreadIdRef, discardAllLiveMessages])

  // Streaming is the production transport; buffered is the test/degradation seam. Either way the turn is
  // registered as the active turn (withActiveTurn) so it's observable + stoppable regardless of initiator.
  const streamFactory = useMemo(() => {
    const base = transportMode === "buffered"
      ? createBufferedChatStream({ postChatMessage })
      : createStreamingChatStream({ openStreamingTurn })
    // A turn whose reply never settles (Stop between the finish tap and the runtime's finish) leaves its canonical
    // text parked; drop it at the next turn rather than let that turn's reply claim it.
    return withActiveTurn((request) => {
      discardParkedFinalAnswer()
      return base(request)
    }, { beginTurn, endTurn, onTurnOutcome: trackTurnOutcome })
  }, [transportMode, openStreamingTurn, postChatMessage, beginTurn, endTurn, discardParkedFinalAnswer, trackTurnOutcome])

  const handleFailedTurnAssistantId = useCallback(
    (assistantMessageId: string, threadId: string | null) => {
      if (!turnBelongsToCurrentView(threadId)) return
      setServerAssistantId(assistantMessageId)
    },
    [turnBelongsToCurrentView],
  )

  const adapter = useMemo(
    () => createChatboxAdapter(streamFactory, {
      onFinishMetadata: handleFinishMetadata,
      onEarlyThreadId: handleEarlyThreadId,
      onFailedTurnAssistantId: handleFailedTurnAssistantId,
      getTurnSignal,
      onTurnSettled: handleTurnSettled,
    }),
    [streamFactory, handleFinishMetadata, handleEarlyThreadId, handleFailedTurnAssistantId, getTurnSignal, handleTurnSettled],
  )

  // Retire a parked prompt AND its on-screen bubble — its turn is over server-side, so history owns whatever
  // committed.
  const discardPendingUserTurn = useCallback((threadId: string, promptId: string) => {
    forgetPendingUserTurn(threadId)
    discardLiveMessage(promptId)
  }, [discardLiveMessage])

  const sendMessage = useCallback(
    async (text: string) => {
      const trimmed = text.trim()
      if (!trimmed) return
      // Don't open a second concurrent turn (composer or programmatic) with a stale guard.
      if (getActiveTurn() != null) return
      const userMessage: ChatMessage = {
        id: v4(),
        role: "user",
        parts: [{ type: "text", text: trimmed }],
        createdAt: new Date().toISOString(),
        status: "sent",
      }
      appendLiveMessage(userMessage)
      // withActiveTurn registers the turn's controller once the adapter opens the stream; capture it to
      // detect a stop/nav abort (the signal passed here is only what the adapter forwards into that controller).
      let turnController: AbortController | null = null
      try {
        const stream = await adapter.sendMessage({
          message: userMessage,
          messages: [...messagesRef.current, userMessage],
          signal: new AbortController().signal,
        })
        turnController = getActiveTurn()
        const assistantMessage = await assembleAssistantMessage(stream, v4())
        // Navigated away / stopped mid-turn: this turn belongs to the view we left, so it must not append here.
        if (turnController?.signal.aborted) return
        // The reply is anchored to this turn's user message; an uncommitted settle (a `cancelled` finish carries no
        // messageId, so it drains cleanly with no client abort) resets the transcript first, and appending onto
        // that reset would strand an orphan assistant bubble.
        appendReplyTo(userMessage.id, assistantMessage)
      } catch (error) {
        // An aborted turn's failure is expected teardown, not a user-facing error.
        if (turnController?.signal.aborted) return
        markLiveMessageErrored(userMessage.id)
        throw error
      }
    },
    [adapter, messagesRef, getActiveTurn, appendLiveMessage, appendReplyTo, markLiveMessageErrored],
  )

  // ---- Resume: reattach an in-flight stream on thread select (attach-on-select) ----------------
  // Probe a selected thread (that isn't live-only) for an active server stream; if one is replaying,
  // assemble the reattached turn into the view. The composer send path is blocked while reattaching.
  // Assembled, not live-token, by design — see the PR notes for the deferred live-reattach.
  // Per-thread lifecycle to dedupe probes WITHIN a single visit (a spurious effect re-run must not open a
  // second probe): 'in-flight' while probing, 'done' once settled. The latch is cleared on explicit
  // (re-)navigation to the thread (navigateToThread), so returning to a thread re-probes — a thread can gain
  // a new in-flight server turn after its first probe (send then nav-away is the resume case itself).
  const resumeStateRef = useRef<Map<string, "in-flight" | "done">>(new Map())
  useEffect(() => {
    const chatId = activeThreadId
    if (transportMode === "buffered" || chatId == null) return undefined
    if (adoptedThreadIdsRef.current.has(chatId)) return undefined
    const resumeState = resumeStateRef.current
    if (resumeState.get(chatId) != null) return undefined
    resumeState.set(chatId, "in-flight")

    const controller = new AbortController()
    let settled = false
    const probe = async () => {
      // Yield so the reattach flag isn't flipped synchronously inside the effect body (react-hooks/set-state-in-effect).
      await Promise.resolve()
      if (controller.signal.aborted) return
      setIsReattaching(true)
      setIsReconnecting(true)
      // Stable key for the reattached turn: progress snapshots and the finished message both upsert under it, so
      // the turn renders token-by-token as one bubble (never stacks) and collapses cleanly once finish lands.
      const fallbackMessageId = v4()
      // The resume turn belongs to THIS view only: drop a progress/final write once the user has navigated away
      // (abort) or moved to another thread — the same guard the finish tap uses.
      const belongsToThisView = () => !controller.signal.aborted && activeThreadIdRef.current === chatId
      // The prompt of the turn being resumed (pending-user-turn): the only copy that survived nav-away. Written
      // before the handshake, so it shows under the "Reconnecting" pill and ahead of every assistant snapshot.
      const pendingUserTurn = getPendingUserTurn(chatId)
      if (pendingUserTurn != null) upsertLiveMessage(pendingUserTurn.id, pendingUserChatMessage(pendingUserTurn))
      try {
        const assistantMessage = await reattachActiveStream(streamingTransport, chatId, controller.signal, fallbackMessageId, {
          // A turn can fail while the client is reattaching. The guard has to move for that path too,
          // or the next send re-parents on the row this failure moved past.
          onFailedTurnAssistantId: (assistantMessageId) => {
            if (belongsToThisView()) setServerAssistantId(assistantMessageId)
          },
          onFinishMetadata: (metadata) => {
            // Resume is always a continue on an EXISTING thread (never adoption), so only the guard slot applies —
            // running the full handleFinishMetadata here could wrongly adopt. Guard on the live thread id, not chatId.
            if (metadata.messageId != null && belongsToThisView()) setServerAssistantId(metadata.messageId)
            if (metadata.messageId == null) return
            // Committed: history owns the prompt from the next visit on. The on-screen bubble STAYS — this visit's
            // history was fetched before the commit, so removing it would make the question vanish at turn end.
            forgetPendingUserTurn(chatId)
            if (pendingUserTurn != null) setResumedPromptPair({ promptId: pendingUserTurn.id, assistantId: metadata.messageId })
          },
          // Render the replay token-by-token as it arrives (APP-3376), keyed by the stable fallback id.
          onProgress: (partial) => {
            if (!belongsToThisView()) return
            setIsReconnecting(false) // first content in — the stream is flowing, so drop the handshake indicator
            upsertLiveMessage(fallbackMessageId, partial)
          },
        })
        // active-stream can return a recently-CLOSED stream whose terminal ALSO arrives via history — dedupe by id.
        // Guard on the live thread id (like the finish tap above): a probe resolving as the user leaves must not
        // append into the newly selected view — the abort may land after this microtask under passive-effect scheduling.
        // Also skip when the stream dropped before any text chunks (parts: []) — nothing to show. The final upsert
        // replaces the last progress snapshot in place (same key), settling the turn on its committed form.
        if (assistantMessage != null && assistantMessage.parts.length > 0 && belongsToThisView()) upsertLiveMessage(fallbackMessageId, assistantMessage)
        // No active stream (204) or empty replay (parts: []): the turn already ended with no renderable content —
        // the parked prompt has no turn to belong to and history holds whatever committed. Retire it rather than
        // leave a question nobody is answering.
        if ((assistantMessage == null || assistantMessage.parts.length === 0) && pendingUserTurn != null) discardPendingUserTurn(chatId, pendingUserTurn.id)
      } catch (error) {
        // Gone server-side (404/410 -> StreamResumeError): same as no active stream, retire the parked prompt. A
        // nav-away abort is NOT that — it keeps the prompt so the next visit re-attaches and shows it again.
        if (error instanceof StreamResumeError) {
          // Thrown on the handshake response (404/410), so no partial can have rendered — only the prompt to retire.
          if (pendingUserTurn != null) discardPendingUserTurn(chatId, pendingUserTurn.id)
        } else if (belongsToThisView()) {
          // A terminal `error` chunk lands AFTER onProgress rendered tokens; leaving that partial standing would
          // read as a finished (silently truncated) answer. Drop it and flag the prompt, as the live-send path
          // does. View-scoped, so a nav-away abort leaves the parked turn alone for the next visit to re-attach.
          discardLiveMessage(fallbackMessageId)
          if (pendingUserTurn != null) markLiveMessageErrored(pendingUserTurn.id)
        }
        // Expected: nav-away abort + no/expired active stream. Anything else is a real fault worth logging.
        if (!isExpectedStreamGoneError(error)) log(error)
      } finally {
        settled = true
        resumeState.set(chatId, "done")
        // View-scoped like setIsReattaching: a quick thread-switch can have the new thread's probe already running;
        // clearing unconditionally would hide the new thread's "Reconnecting" pill.
        if (activeThreadIdRef.current === chatId) setIsReconnecting(false)
        if (activeThreadIdRef.current === chatId) setIsReattaching(false)
      }
    }
    void probe()
    return () => {
      controller.abort()
      // Aborted before it resolved: drop the latch so a later re-select re-probes; stop signalling reattach.
      if (!settled) {
        resumeState.delete(chatId)
        setIsReattaching(false)
        setIsReconnecting(false)
      }
    }
  }, [transportMode, activeThreadId, surfaceSeq, streamingTransport, upsertLiveMessage, discardPendingUserTurn, discardLiveMessage, markLiveMessageErrored, activeThreadIdRef, adoptedThreadIdsRef])

  // ---- Navigation ------------------------------------------------------------------------------

  // Clear a live-only thread's marker when leaving so the next open refetches its history; reads the ref mirror to cover a turn that just finished this tick.
  const forgetLiveOnlyThread = useCallback((threadId: string | null) => {
    if (threadId == null || !adoptedThreadIdsRef.current.has(threadId)) return
    setAdoptedThreadIds((current) => {
      if (!current.has(threadId)) return current
      const next = new Set(current)
      next.delete(threadId)
      return next
    })
  }, [adoptedThreadIdsRef])

  const navigateToThread = useCallback(
    (threadId: string | null) => {
      // Re-selecting the already-active thread is a no-op: the sidebar fires selectThread on every
      // click, so a double-click must not abort an in-flight turn or flash the pane empty. "New chat"
      // (null) still resets, so an in-flight not-yet-adopted send can be abandoned by starting fresh.
      // Compare the LIVE id (ref), not the closure — the closure lags a commit, so a second click landing
      // in the same frame as the first would otherwise re-navigate and abort the turn it just started.
      if (threadId != null && threadId === activeThreadIdRef.current) return
      // Abort any in-flight turn before switching so its result can't adopt or append to the new view. Invariant:
      // nav DETACHES only (client read) — it never calls stopServerStream, so the server generation survives for
      // attach-on-select resume. Server cancel stays scoped to the composer's explicit Stop (stopServerStream).
      stopTurn()
      const leavingThreadId = activeThreadId
      forgetLiveOnlyThread(leavingThreadId)
      // Live turns exist only in liveMessages, never the shared history cache, and nav-away wipes them.
      // Drop the leaving thread's cache entry so the next open refetches full history including those
      // turns — this covers an adopted thread too, whose entry another surface (ADR 0007) may have
      // written stale; without it the present-but-stale cache gates off the refetch and the turns vanish.
      if (leavingThreadId != null && liveMessagesRef.current.length > 0) {
        queryClient.removeQueries({ queryKey: getMessagesQueryKey(accountAlias, leavingThreadId), exact: true })
      }
      // Clear the destination's resume latch so a re-select re-probes for a (possibly new) in-flight stream.
      if (threadId != null) resumeStateRef.current.delete(threadId)
      setServerAssistantId(null)
      setActiveThreadId(threadId)
      // navKey bumps below, remounting ChatBox — it can no longer report the discarded turn, so stop suppressing.
      resetLiveMessagesForNavigation()
      setNavSeq((sequence) => sequence + 1)
    },
    [stopTurn, forgetLiveOnlyThread, activeThreadId, queryClient, accountAlias, activeThreadIdRef, liveMessagesRef, resetLiveMessagesForNavigation],
  )

  const selectThread = useCallback((threadId: string) => {
    navigateToThread(threadId)
  }, [navigateToThread])
  // New chat: navigate to the empty state. Unlike a thread select this always resets, so it can abandon an
  // in-flight not-yet-adopted send.
  const startNewThread = useCallback(() => {
    navigateToThread(null)
  }, [navigateToThread])

  // The provider outlives a visit now, so a seed can arrive with ChatBox already mounted. The draft bridge
  // restores only on its own mount or a draftKey change, so bump navKey — navigateToThread(null) always does.
  // That bump is destructive (it aborts the turn and wipes the transcript), so it is earned, not automatic:
  // a seed that lost to existing text, or would interrupt a turn the user is watching, does not navigate.
  const seedComposerDraft = useCallback((text: string) => {
    const seeded = seedNewChatDraft(accountAlias, text)
    // Strictly "a turn is still running": stopTurn aborts but only endTurn nulls the ref, so a plain non-null
    // check would silently bank the seed for the microtasks between a detach-abort and its settle.
    const isTurnLive = getActiveTurn()?.signal.aborted === false
    if (!seeded || isTurnLive) return
    navigateToThread(null)
  }, [accountAlias, navigateToThread, getActiveTurn])

  // ---- Surface presence ------------------------------------------------------------------------
  // The controller now outlives the transcript surface (ADR 0018), so the surface's own mount/unmount is a
  // lifecycle event the controller has to hear about. ChatBox unmounting is what the MUI runtime aborts the
  // in-flight turn on, and nothing else in the resume effect's deps changes across a remount onto one thread.

  // `surfaceCountRef` (declared above, with the other refs) counts mounted transcript surfaces. WP3/WP4 briefly
  // allow a dock and the route to coexist, and the first to unmount must not abort a turn the other is rendering.
  // The thread whose history went stale because a visit left uncommitted turns behind. Consumed by the NEXT
  // attach, never at detach: the turn is still generating when the user leaves, and op-summarize writes the
  // user row only on commit — so a refetch at detach caches a page with neither the question nor the answer,
  // and staleTime: Infinity pins it. The refetch belongs to the visit that can actually see the commit.
  const staleHistoryThreadIdRef = useRef<string | null>(null)

  const attachSurface = useCallback(() => {
    surfaceCountRef.current += 1
    // WP3/WP4 tripwire: two surfaces on one controller collide on the draft key and the transcript scroller.
    if (surfaceCountRef.current > 1 && process.env.NODE_ENV !== "production") {
      log(new Error("Two Kelly transcript surfaces are mounted on one controller"))
    }
    const staleThreadId = staleHistoryThreadIdRef.current
    staleHistoryThreadIdRef.current = null
    if (staleThreadId != null) {
      queryClient.removeQueries({ queryKey: getMessagesQueryKey(accountAlias, staleThreadId), exact: true })
      // Cleared on ATTACH, never at detach: this marker doubles as a just-created thread's exemption from
      // deleted-thread reconciliation, which has to hold for the whole detached window. Its live turns are
      // gone, so from here history is the only way back to the conversation.
      forgetLiveOnlyThread(staleThreadId)
    }
    // A freshly mounted ChatBox cannot report the previous visit's turns, so stop suppressing their ids —
    // otherwise a resumed prompt reusing one would be filtered straight back out of liveMessages.
    clearDiscardedLiveIds()
    setSurfaceSeq((sequence) => sequence + 1)
  }, [queryClient, accountAlias, clearDiscardedLiveIds, forgetLiveOnlyThread])

  const detachSurface = useCallback(() => {
    surfaceCountRef.current = Math.max(0, surfaceCountRef.current - 1)
    if (surfaceCountRef.current > 0) return
    // A turn's lifetime is the surface's visit. The runtime already aborts the composer's turn on unmount;
    // aborting here as well is what gives the programmatic path (sendMessage) the same answer.
    stopTurn()
    const threadId = activeThreadIdRef.current
    // Thread-scoped bookkeeping only. The discard below is NOT: a detach before `thread_created` has no thread
    // id yet, and handleTurnSettled cannot cover it either (it bails when the turn adopted nothing), so an
    // early return here leaves the optimistic prompt and a truncated partial to render as a settled turn.
    if (threadId != null) {
      // Per-thread latch: clear it or the next visit to this same thread never re-probes for a live stream.
      resumeStateRef.current.delete(threadId)
      if (liveMessagesRef.current.length > 0) staleHistoryThreadIdRef.current = threadId
    }
    if (liveMessagesRef.current.length === 0) return
    // Drop the live turns: an aborted turn's partial would otherwise come back rendered in settled chrome,
    // reading as a finished answer. Suppress their ids (as handleTurnSettled does) rather than clear the set —
    // there is no navKey bump here, so a runtime onMessagesChange landing after the abort would restore them.
    discardAllLiveMessages()
  }, [stopTurn, activeThreadIdRef, liveMessagesRef, discardAllLiveMessages])

  // Once loaded, an active thread missing from the list was deleted elsewhere — reset to new-thread (guarded render-time derived state).
  // Threads adopted this session are exempt: a lagging list refetch must not tear down a just-created conversation.
  // Clearing the continue guard IS a render-legal state adjust and must happen here so it can't leak into the fresh
  // empty state; aborting an in-flight turn on the deleted thread is the one remaining side effect, handled by the
  // nav effect below.
  const activeThreadDeleted = isThreadsLoaded && !isThreadsErrored && activeThreadId != null
    && !threads.some((thread) => thread.threadId === activeThreadId)
    && !adoptedThreadIds.has(activeThreadId)
  if (activeThreadDeleted) {
    setActiveThreadId(null)
    clearLiveMessages()
    setServerAssistantId(null)
    setNavSeq((sequence) => sequence + 1)
  }

  // ---- Delete reconciliation (the mutation itself lives in useThreadList) ----------------------

  // Fire-and-forget (PROD-452): tear the thread down locally now — the DELETE can take minutes in production.
  // A rollback restores the sidebar row only; the dropped message cache refetches on re-select.
  const deleteThread = useCallback(
    (threadId: string) => {
      deleteThreadMutation({ accountAlias, threadId })
      setAdoptedThreadIds((current) => {
        if (!current.has(threadId)) return current
        const next = new Set(current)
        next.delete(threadId)
        return next
      })
      queryClient.removeQueries({ queryKey: getMessagesQueryKey(accountAlias, threadId), exact: true })
      forgetComposerDraft(accountAlias, threadId)
      // Deleting the conversation you are viewing drops to the New-chat empty state. Read the active thread
      // FRESH (the ref, not the render closure) so deleting a background row never moves the view.
      if (threadId === activeThreadIdRef.current) navigateToThread(null)
    },
    [deleteThreadMutation, accountAlias, queryClient, navigateToThread, activeThreadIdRef],
  )

  // This object IS the context value, and the provider is app-level now (ADR 0018), so its identity propagates
  // through every mounted page rather than just the chat route. The memo only holds while nothing exposed
  // changed — `messages` is in the deps, so a streaming turn still publishes a new value per token. Making
  // THAT cheap needs the value split (stable actions vs. the transcript); this is the floor, not the fix.
  return useMemo(() => ({
    threads,
    isThreadsLoaded,
    isThreadsErrored,
    activeThreadId,
    readDraft,
    writeDraft,
    navKey: String(navSeq),
    messages,
    onMessagesChange,
    adapter,
    selectThread,
    startNewThread,
    sendMessage,
    isTurnActive,
    stopTurn,
    stopServerStream,
    attachSurface,
    detachSurface,
    isReattaching,
    isReconnecting,
    hasOlder: !!activeThreadId && !!hasNextPage,
    isLoadingHistory: isFetchingHistory && historyData === undefined,
    isLoadingOlder: isFetchingNextPage,
    historyError,
    loadOlder: fetchNextPage,
    renameThread,
    seedComposerDraft,
    pinThread,
    canPinThreads,
    deleteThread,
    mutatingThreadId,
  }), [
    threads, isThreadsLoaded, isThreadsErrored, activeThreadId, readDraft, writeDraft, navSeq, messages,
    onMessagesChange, adapter, selectThread, startNewThread, sendMessage, isTurnActive, stopTurn,
    stopServerStream, attachSurface, detachSurface, isReattaching, isReconnecting, hasNextPage,
    isFetchingHistory, historyData, isFetchingNextPage, historyError, fetchNextPage, renameThread,
    seedComposerDraft, pinThread, canPinThreads, deleteThread, mutatingThreadId,
  ])
}
